import { getAuditTrailData, extractVersionStructure } from '../../../../util/getAuditTrailData';
import { generateNodeIndexing } from '../../../../hooks/useNodeIndexing';
import { generateTitlesFromStructure } from '../../../../util/AppUtils';
import { convertAbsoluteToRelative } from '../../../Canvas/components/arrow/ArrowElement';

const CHILD_NODE_ID_FIELDS = [
  'attachment_tree_node_id',
  'video_tree_node_id',
  'rich_text_node_id',
  'canvas_tree_node_id',
  'form_tree_node_id',
];

const CHILD_TYPE_MAP = {
  attachment: 'Attachment',
  canvas: 'Canvas',
  'rich-text': 'Rich Text',
  video: 'Video',
  form: 'Form',
};

const normalizeChildType = (type = '') => {
  const lowerType = type.toLowerCase();
  if (lowerType === 'canva') return 'canvas';
  if (lowerType === 'richtext') return 'rich-text';
  return lowerType;
};

const getChildTypeDisplayName = (type = '') => {
  const normalized = normalizeChildType(type);
  return CHILD_TYPE_MAP[normalized] || (normalized ? normalized.charAt(0).toUpperCase() + normalized.slice(1) : 'Element');
};

const toLowerCaseFirst = (value) => {
  if (!value) return value;
  return value.charAt(0).toLowerCase() + value.slice(1);
};

const getSiblingOrderNumber = (node) => {
  if (!node) return 0;
  const order =
    node.sibling_order ??
    node.procedure_tree?.sibling_order ??
    node.detail?.procedure_tree?.sibling_order ??
    '0';
  const parsed = parseInt(order, 10);
  return Number.isNaN(parsed) ? 0 : parsed;
};

const getChildNodeId = (child) => {
  if (!child) return null;
  if (child.tree_node_id) return child.tree_node_id;
  if (child.node_id) return child.node_id;
  
  const detail = child.detail || {};
  for (const field of CHILD_NODE_ID_FIELDS) {
    if (detail[field]) return detail[field];
  }
  return null;
};

const getNodeId = (node, idFields = ['tree_node_id', 'section_id_ref', 'step_id_ref', 'substep_id_ref', 'node_id']) => {
  if (!node) return null;
  for (const field of idFields) {
    if (node[field]) return node[field];
  }
  return null;
};

const collectStepHierarchyNodes = (step) => {
  const nodes = [];
  if (!step) return nodes;
  
  const stepId = getNodeId(step, ['tree_node_id', 'step_id_ref', 'node_id']);
  if (stepId) {
    nodes.push({ id: stepId, node: step, type: 'step' });
  }

  const parentStepId = stepId;

  // Collect step children
  const sortedStepChildren = [...(step.children || [])].sort(
    (a, b) => getSiblingOrderNumber(a) - getSiblingOrderNumber(b)
  );
  sortedStepChildren.forEach((child) => {
    if (child?.is_deleted) return;
    const childId = getChildNodeId(child);
    if (!childId) return;
    nodes.push({ id: childId, node: child, type: 'stepChild', parentStepId });
  });

  // Collect substeps and their children
  const sortedSubsteps = [...(step.substeps || [])].sort(
    (a, b) => getSiblingOrderNumber(a) - getSiblingOrderNumber(b)
  );
  sortedSubsteps.forEach((substep) => {
    if (!substep || substep.is_deleted) return;
    const substepId = getNodeId(substep, ['tree_node_id', 'substep_id_ref', 'node_id']);
    if (substepId) {
      nodes.push({ id: substepId, node: substep, type: 'substep', parentStepId });
    }

    const parentSubstepId = substepId;
    const sortedSubstepChildren = [...(substep.children || [])].sort(
      (a, b) => getSiblingOrderNumber(a) - getSiblingOrderNumber(b)
    );
    sortedSubstepChildren.forEach((child) => {
      if (child?.is_deleted) return;
      const childId = getChildNodeId(child);
      if (!childId) return;
      nodes.push({ id: childId, node: child, type: 'substepChild', parentSubstepId, parentStepId });
    });
  });

  return nodes;
};

const collectSectionHierarchyNodes = (section) => {
  const nodes = [];
  if (!section) return nodes;
  
  const sectionId = getNodeId(section, ['tree_node_id', 'section_id_ref', 'node_id']);
  if (sectionId) {
    nodes.push({ id: sectionId, node: section, type: 'section' });
  }

  // Collect steps and their hierarchy FIRST (before section children)
  // Order: Section -> Step 1 -> Step 1 children -> Step 2 -> Step 2 children -> Section children
  const sortedSteps = [...(section.steps || [])].sort(
    (a, b) => getSiblingOrderNumber(a) - getSiblingOrderNumber(b)
  );
  sortedSteps.forEach((step) => {
    if (!step || step.is_deleted) return;
    const hierarchy = collectStepHierarchyNodes(step);
    nodes.push(...hierarchy);
  });

  // Collect section children AFTER all steps
  const sortedSectionChildren = [...(section.children || [])].sort(
    (a, b) => getSiblingOrderNumber(a) - getSiblingOrderNumber(b)
  );
  sortedSectionChildren.forEach((child) => {
    if (child?.is_deleted) return;
    const childId = getChildNodeId(child);
    if (!childId) return;
    nodes.push({ id: childId, node: child, type: 'sectionChild', parentSectionId: sectionId });
  });

  return nodes;
};

const resolveNodeNameWithFallback = (id, originalIndexMap, node, defaultLabel) => {
  if (id && originalIndexMap?.[id]) return originalIndexMap[id];
  
  if (node) {
    const possibleFields = [
      'title', 'name', 'section_name', 'sectionTitle',
      'step_name', 'stepTitle', 'substep_name', 'label', 'display_name'
    ];
    for (const field of possibleFields) {
      if (node[field]) return node[field];
    }
    
    if (node.detail) {
      const detailFields = ['title', 'name', 'label', 'display_name'];
      for (const field of detailFields) {
        if (node.detail[field]) return node.detail[field];
      }
    }
  }
  return defaultLabel;
};

const ensureSequentialSectionOrder = (sections = []) => {
  sections.forEach((section, index) => {
    if (!section) return;
    const orderValue = index.toString();
    section.sibling_order = orderValue;
    if (section.procedure_tree) {
      section.procedure_tree.sibling_order = orderValue;
    }
  });
};

const cloneStructure = (structure = []) => {
  if (!Array.isArray(structure)) return [];
  return structuredClone(structure);
};

const setBlankSection = (sections = [], sourceSection) => {
  if (!sourceSection || !Array.isArray(sections)) return sections || [];
  const sourceSectionId = getNodeId(sourceSection, ['tree_node_id', 'section_id_ref']);
  if (!sourceSectionId) return sections;
  
  // Create a deep copy to avoid mutating the original array
  const clonedSections = structuredClone(sections);
  
  // Find the source section and blank its steps array
  const sourceSectionIndex = clonedSections.findIndex(
    section => getNodeId(section, ['tree_node_id', 'section_id_ref']) === sourceSectionId
  );
  
  if (sourceSectionIndex !== -1) {
    clonedSections[sourceSectionIndex].steps = [];
  }
  
  return clonedSections;
};

// Helper to extract document info
const getDocumentInfo = (doc, fallbackName = 'Document') => ({
  partNumber: doc?.partNumber || doc?.part_number || doc?.partId || fallbackName,
  revision: doc?.revision || doc?.revisionLabel || doc?.revision_id || '-',
});

// Helper to find section in array
const findSectionById = (sections, targetId) => {
  return sections.find(
    section => section.tree_node_id === targetId || section.section_id_ref === targetId
  ) || null;
};

// Helper to find final section after copy/move
const findFinalSection = (newContent, sourceSection, position, movePosition, isCopyOperation = false) => {
  // For copy operations, we need to find the COPIED section, not the original
  // The copied section will be at a different position than the original
  if (isCopyOperation) {
    // For copy operations, find by position (the copied section)
    if (position === 'into') {
      const sortedSections = [...newContent].sort((a, b) => {
        const orderA = getSiblingOrderNumber(a);
        const orderB = getSiblingOrderNumber(b);
        return orderA - orderB;
      });
      // For same-document copy, the copied section should be at the end
      // Count how many sections match the source section's ID
      const sourceSectionId = getNodeId(sourceSection, ['tree_node_id', 'section_id_ref']);
      const matchingSections = sortedSections.filter(section => 
        getNodeId(section, ['tree_node_id', 'section_id_ref']) === sourceSectionId
      );
      // If there are multiple matching sections (original + copy), return the last one (the copy)
      // The copy will have a higher sibling_order than the original
      if (matchingSections.length > 1) {
        return matchingSections[matchingSections.length - 1];
      }
      // If only one matching section, it might be a cross-document copy (no original in target)
      // or the sections have different IDs already. Return the last section in sorted order.
      return sortedSections.length > 0 ? sortedSections[sortedSections.length - 1] : null;
    }
    
    if (movePosition?.targetId) {
      const sortedSections = [...newContent].sort((a, b) => {
        const orderA = getSiblingOrderNumber(a);
        const orderB = getSiblingOrderNumber(b);
        return orderA - orderB;
      });
      const targetIndex = sortedSections.findIndex(
        section => getNodeId(section, ['tree_node_id', 'section_id_ref']) === movePosition.targetId
      );
      if (targetIndex !== -1) {
        if (position === 'above') {
          // For copy above, the copied section should be at targetIndex - 1
          // But we need to find the one that matches sourceSection (the copy)
          const candidateSection = targetIndex > 0 ? sortedSections[targetIndex - 1] : null;
          if (candidateSection && 
              getNodeId(candidateSection, ['tree_node_id', 'section_id_ref']) === 
              getNodeId(sourceSection, ['tree_node_id', 'section_id_ref'])) {
            return candidateSection;
          }
        }
        if (position === 'below') {
          // For copy below, the copied section should be at targetIndex + 1
          const candidateSection = targetIndex < sortedSections.length - 1 ? sortedSections[targetIndex + 1] : null;
          if (candidateSection && 
              getNodeId(candidateSection, ['tree_node_id', 'section_id_ref']) === 
              getNodeId(sourceSection, ['tree_node_id', 'section_id_ref'])) {
            return candidateSection;
          }
        }
      }
    }
  } else {
    // For move operations, try to find by matching tree_node_id first
    let finalSection = findSectionById(newContent, sourceSection.tree_node_id || sourceSection.section_id_ref);
    if (finalSection) return finalSection;
    
    // Fallback to position-based finding for moves
    if (position === 'into') {
      const sortedSections = [...newContent].sort((a, b) => {
        const orderA = getSiblingOrderNumber(a);
        const orderB = getSiblingOrderNumber(b);
        return orderA - orderB;
      });
      return sortedSections.length > 0 ? sortedSections[sortedSections.length - 1] : null;
    }
    
    if (movePosition?.targetId) {
      const targetIndex = newContent.findIndex(
        section => section.tree_node_id === movePosition.targetId
      );
      if (targetIndex !== -1) {
        if (position === 'above' && targetIndex > 0) {
          return newContent[targetIndex - 1];
        }
        if (position === 'below' && targetIndex < newContent.length - 1) {
          return newContent[targetIndex + 1];
        }
      }
    }
  }
  
  return null;
};

// Helper to build lookup maps
const buildLookupMaps = (section) => {
  const stepLookup = new Map();
  const substepLookup = new Map();
  
  (section.steps || []).forEach((step) => {
    if (!step || step.is_deleted) return;
    const stepId = getNodeId(step, ['tree_node_id', 'step_id_ref']);
    if (stepId) stepLookup.set(stepId, step);
    
    (step.substeps || []).forEach((substep) => {
      if (!substep || substep.is_deleted) return;
      const substepId = getNodeId(substep, ['tree_node_id', 'substep_id_ref']);
      if (substepId) substepLookup.set(substepId, substep);
    });
  });
  
  return { stepLookup, substepLookup };
};

// Helper to collect all possible IDs for a node
const collectPossibleIds = (id, node) => {
  const possibleIds = [id].filter(Boolean);
  
  if (!node) return possibleIds;
  
  const idFields = ['tree_node_id', 'section_id_ref', 'step_id_ref', 'substep_id_ref', 'node_id'];
  idFields.forEach(field => {
    if (node[field] && node[field] !== id) {
      possibleIds.push(node[field]);
    }
  });
  
  if (node.detail) {
    CHILD_NODE_ID_FIELDS.forEach(field => {
      if (node.detail[field]) possibleIds.push(node.detail[field]);
    });
  }
  
  return possibleIds;
};

// Helper to resolve original name
const resolveOriginalName = (item, originalIndexMap, finalIndexMap, childTypeCounters) => {
  const { id, node, type } = item;
  
  const defaultLabel = type === 'section' ? 'Section'
    : type === 'step' ? 'Step'
    : type === 'substep' ? 'Substep'
    : getChildTypeDisplayName(node?.type);

  const possibleIds = collectPossibleIds(id, node);
  
  // For child nodes, use counter-based naming
  if (type === 'sectionChild' || type === 'stepChild' || type === 'substepChild') {
    const normalizedChildType = normalizeChildType(node?.type);
    if (normalizedChildType && childTypeCounters.hasOwnProperty(normalizedChildType)) {
      childTypeCounters[normalizedChildType] += 1;
      const childDisplayName = getChildTypeDisplayName(normalizedChildType);
      return `${childDisplayName} ${childTypeCounters[normalizedChildType]}`;
    }
  }
  
  // Try to find in index maps
  const mappedId = possibleIds.find(id => id && (originalIndexMap[id] || finalIndexMap[id]));
  if (mappedId) {
    return originalIndexMap[mappedId] || finalIndexMap[mappedId];
  }
  
  return resolveNodeNameWithFallback(id, originalIndexMap, node, defaultLabel);
};

// Helper to build movement phrase
const buildMovementPhrase = (type, position, destinationBase, finalSectionName, finalStepName, finalSubstepName, targetSectionFinalName) => {
  switch (type) {
    case 'section':
      if (position === 'into') {
        return `into ${destinationBase}`;
      }
      const positionWord = position === 'above' ? 'above' : 'below';
      return `${positionWord} ${targetSectionFinalName || 'the target section'} in ${destinationBase}`;
    case 'sectionChild':
    case 'step':
      return `into ${finalSectionName || 'the target section'} in ${destinationBase}`;
    case 'stepChild':
      return `into ${finalStepName} in ${destinationBase}`;
    case 'substep':
      return `into ${finalStepName} in ${destinationBase}`;
    case 'substepChild':
      return `into ${finalSubstepName} in ${destinationBase}`;
    default:
      return `into ${destinationBase}`;
  }
};

const stripUnaddedChildrenForCopy = (node, type) => {
  if (!node) return null;
  const cloned = JSON.parse(JSON.stringify(node));
  if (type === 'section') {
    delete cloned.steps;
    delete cloned.children;
  } else if (type === 'step') {
    delete cloned.substeps;
    delete cloned.children;
  } else if (type === 'substep') {
    delete cloned.children;
  }
  return cloned;
};

const findNodeInSectionForCopy = (section, nodeId, type) => {
  if (!section || !nodeId) return null;
  const sectionId = getNodeId(section, ['tree_node_id', 'section_id_ref']);
  if (type === 'section' && sectionId === nodeId) {
    return section;
  }

  const searchChildren = (collection = [], predicate) => {
    for (const item of collection) {
      if (!item) continue;
      if (predicate(item)) {
        return item;
      }
    }
    return null;
  };

  const sectionChild = searchChildren(section.children, child => getChildNodeId(child) === nodeId && type === 'sectionChild');
  if (sectionChild) return sectionChild;

  if (section.steps) {
    for (const step of section.steps) {
      if (!step) continue;
      const stepId = getNodeId(step, ['tree_node_id', 'step_id_ref']);
      if (type === 'step' && stepId === nodeId) {
        return step;
      }

      const stepChild = searchChildren(step.children, child => getChildNodeId(child) === nodeId && type === 'stepChild');
      if (stepChild) return stepChild;

      if (step.substeps) {
        for (const substep of step.substeps) {
          if (!substep) continue;
          const substepId = getNodeId(substep, ['tree_node_id', 'substep_id_ref']);
          if (type === 'substep' && substepId === nodeId) {
            return substep;
          }

          const substepChild = searchChildren(substep.children, child => getChildNodeId(child) === nodeId && type === 'substepChild');
          if (substepChild) return substepChild;
        }
      }
    }
  }

  return null;
};

const determineSectionInsertIndex = (sections = [], position, movePosition) => {
  if (!Array.isArray(sections) || sections.length === 0) return 0;
  if (position === 'into' || !movePosition?.targetId) {
    return sections.length;
  }

  const targetIndex = sections.findIndex(
    section => getNodeId(section, ['tree_node_id', 'section_id_ref']) === movePosition.targetId
  );

  if (targetIndex === -1) return sections.length;
  return position === 'above' ? targetIndex : targetIndex + 1;
};

const addNodeToSectionStructure = ({
  currentStructure = [],
  hierarchyNode,
  finalSection,
  position,
  movePosition,
  isCopyOperation = false,
}) => {
  const clonedStructure = JSON.parse(JSON.stringify(currentStructure || []));
  const sectionId = getNodeId(finalSection, ['tree_node_id', 'section_id_ref']);
  if (!sectionId) return clonedStructure;

  const ensureSectionExists = () => {
    // First, check if section already exists in the structure
    // For copy operations, we need to find the COPIED section, not the original
    // The copied section will have a dummy tree_node_id starting with "copy-"
    let section = null;
    if (isCopyOperation) {
      // For copy operations, ALWAYS prioritize finding the section with dummy tree_node_id first
      // This ensures we find the new copied section, not the original
      section = clonedStructure.find(sec => 
        sec.tree_node_id && sec.tree_node_id.startsWith('copy-')
      );
      
      // If not found by dummy tree_node_id, try to find by matching source section ID
      if (!section) {
        const sourceSectionId = getNodeId(finalSection, ['tree_node_id', 'section_id_ref']);
        const matchingSections = clonedStructure.filter(sec =>
          getNodeId(sec, ['tree_node_id', 'section_id_ref']) === sourceSectionId
        );
        
        if (matchingSections.length > 1) {
          // Multiple matching sections (original + copy) - find the one with dummy tree_node_id
          section = matchingSections.find(sec => 
            sec.tree_node_id && sec.tree_node_id.startsWith('copy-')
          );
          // If no dummy tree_node_id found, sort by sibling_order to find the one that was added
          if (!section) {
            const sortedMatching = [...matchingSections].sort((a, b) => {
              const orderA = getSiblingOrderNumber(a);
              const orderB = getSiblingOrderNumber(b);
              return orderB - orderA; // Descending - get the one with highest order (the copy)
            });
            section = sortedMatching[0]; // The one with highest sibling_order is the copy
          }
        } else if (matchingSections.length === 1) {
          // Only one matching section - check if it has dummy tree_node_id, otherwise it might be the original
          const candidate = matchingSections[0];
          if (candidate.tree_node_id && candidate.tree_node_id.startsWith('copy-')) {
            section = candidate;
          }
          // If it doesn't have dummy tree_node_id, don't use it (it's the original section)
        }
      }
    } else {
      // For move operations, check by sectionId
      section = clonedStructure.find(sec =>
        getNodeId(sec, ['tree_node_id', 'section_id_ref']) === sectionId
      );
    }
    
    if (section) {
      // Section already exists - return it (don't add again)
      // For the first operation (type === 'section'), ensure it's empty
      if (type === 'section') {
        delete section.steps;
        delete section.children;
      }
      return section;
    }
    
    // Section doesn't exist - add it
    // For copy operations, this happens in the first operation (adding the section itself)
    // For move operations, this shouldn't happen (section should be removed first)
    const strippedSection = stripUnaddedChildrenForCopy(finalSection, 'section');
    // Ensure the section is truly empty (no steps, no children)
    if (strippedSection) {
      delete strippedSection.steps;
      delete strippedSection.children;
    }
    const insertIndex = determineSectionInsertIndex(clonedStructure, position, movePosition);
    clonedStructure.splice(insertIndex, 0, strippedSection);
    ensureSequentialSectionOrder(clonedStructure);
    return strippedSection;
  };

  const { type, id, parentStepId, parentSubstepId } = hierarchyNode;

  if (type === 'section') {
    // For the first operation (adding the section itself), always add a new section
    // Don't check if it exists - we're creating a new copy
    const strippedSection = stripUnaddedChildrenForCopy(finalSection, 'section');
    if (strippedSection) {
      delete strippedSection.steps;
      delete strippedSection.children;
      // For copy operations, assign a dummy unique tree_node_id to track the new copied section
      // This helps us distinguish it from the original section in same-document copies
      if (isCopyOperation) {
        // Generate a dummy unique tree_node_id (using timestamp + random to ensure uniqueness)
        strippedSection.previous_tree_node_id = strippedSection.tree_node_id;
        strippedSection.tree_node_id = `copy-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      }
    }
    const insertIndex = determineSectionInsertIndex(clonedStructure, position, movePosition);
    clonedStructure.splice(insertIndex, 0, strippedSection);
    ensureSequentialSectionOrder(clonedStructure);
    return clonedStructure;
  }

  // For subsequent operations (adding steps, children, etc.), find the existing section
  const targetSection = ensureSectionExists();
  const nodeData = findNodeInSectionForCopy(finalSection, id, type);
  if (!nodeData) return clonedStructure;
  const clonedNode = stripUnaddedChildrenForCopy(nodeData, type);
  if (!clonedNode) return clonedStructure;

  // For copy operations, preserve the original tree_node_id as previous_tree_node_id
  if (isCopyOperation && clonedNode.tree_node_id) {
    clonedNode.previous_tree_node_id = clonedNode.tree_node_id;
  }

  if (type === 'sectionChild') {
    if (!targetSection.children) targetSection.children = [];
    // Check if child already exists to avoid duplicates
    const existingChild = targetSection.children.find(child => getChildNodeId(child) === id);
    if (!existingChild) {
      targetSection.children.push(clonedNode);
      targetSection.children.forEach((child, index) => {
        child.sibling_order = index.toString();
      });
    }
  } else if (type === 'step') {
    if (!targetSection.steps) targetSection.steps = [];
    // Check if step already exists
    const existingStepIndex = targetSection.steps.findIndex(step =>
      getNodeId(step, ['tree_node_id', 'step_id_ref']) === id
    );
    if (existingStepIndex === -1) {
      // Step doesn't exist, add it (already stripped of children)
      targetSection.steps.push(clonedNode);
      targetSection.steps.forEach((step, index) => {
        step.sibling_order = index.toString();
      });
    } else {
      // Step already exists, ensure it has no children/substeps (shouldn't happen in normal flow, but handle it)
      const existingStep = targetSection.steps[existingStepIndex];
      delete existingStep.children;
      delete existingStep.substeps;
    }
  } else if (type === 'stepChild') {
    const parentStep = (targetSection.steps || []).find(step =>
      getNodeId(step, ['tree_node_id', 'step_id_ref']) === parentStepId
    );
    if (parentStep) {
      if (!parentStep.children) parentStep.children = [];
      // Check if child already exists to avoid duplicates
      const existingChild = parentStep.children.find(child => getChildNodeId(child) === id);
      if (!existingChild) {
        parentStep.children.push(clonedNode);
        parentStep.children.forEach((child, index) => {
          child.sibling_order = index.toString();
        });
      }
    }
  } else if (type === 'substep') {
    const parentStep = (targetSection.steps || []).find(step =>
      getNodeId(step, ['tree_node_id', 'step_id_ref']) === parentStepId
    );
    if (parentStep) {
      if (!parentStep.substeps) parentStep.substeps = [];
      // Check if substep already exists
      const existingSubstepIndex = parentStep.substeps.findIndex(substep =>
        getNodeId(substep, ['tree_node_id', 'substep_id_ref']) === id
      );
      if (existingSubstepIndex === -1) {
        // Substep doesn't exist, add it (already stripped of children)
        parentStep.substeps.push(clonedNode);
        parentStep.substeps.forEach((substep, index) => {
          substep.sibling_order = index.toString();
        });
      } else {
        // Substep already exists, ensure it has no children (shouldn't happen in normal flow, but handle it)
        const existingSubstep = parentStep.substeps[existingSubstepIndex];
        delete existingSubstep.children;
      }
    }
  } else if (type === 'substepChild') {
    const parentStep = (targetSection.steps || []).find(step =>
      getNodeId(step, ['tree_node_id', 'step_id_ref']) === parentStepId
    );
    const parentSubstep = parentStep?.substeps?.find(substep =>
      getNodeId(substep, ['tree_node_id', 'substep_id_ref']) === parentSubstepId
    );
    if (parentSubstep) {
      if (!parentSubstep.children) parentSubstep.children = [];
      // Check if child already exists to avoid duplicates
      const existingChild = parentSubstep.children.find(child => getChildNodeId(child) === id);
      if (!existingChild) {
        parentSubstep.children.push(clonedNode);
        parentSubstep.children.forEach((child, index) => {
          child.sibling_order = index.toString();
        });
      }
    }
  }

  return clonedStructure;
};

// Shared logic for building node indexing
const buildNodeIndexingCore = ({
  sourceSection,
  sourceAllNodes,
  newContent,
  targetAllNodes = [],
  targetDoc,
  sourceDoc,
  position,
  movePosition,
  actionType, // 'Move' or 'Copy'
  buildActionPrefix,
}) => {
  if (!sourceSection || !Array.isArray(newContent) || newContent.length === 0) {
    return null;
  }

  const originalIndexMap = generateNodeIndexing(sourceAllNodes || []);
  const finalIndexMap = generateNodeIndexing(newContent);
  
  // For copy operations, generate index map from original target structure
  const isCopyOperation = actionType === 'Copy';
  const targetIndexMap = isCopyOperation && targetAllNodes.length > 0
    ? generateNodeIndexing(targetAllNodes)
    : finalIndexMap;
  
  const finalSection = findFinalSection(newContent, sourceSection, position, movePosition);
  if (!finalSection) return null;

  const targetInfo = getDocumentInfo(targetDoc, 'Target Document');
  const sourceInfo = sourceDoc ? getDocumentInfo(sourceDoc, 'Source Document') : null;
  
  const originalSectionName = resolveNodeNameWithFallback(
    getNodeId(sourceSection, ['tree_node_id', 'section_id_ref']),
    originalIndexMap,
    sourceSection,
    'Section'
  );

  // For copy operations, find the copied section first to get correct name
  const finalSectionForName = actionType === 'Copy' 
    ? findFinalSection(newContent, sourceSection, position, movePosition, true) || finalSection
    : finalSection;

  const finalSectionName = finalIndexMap[finalSectionForName?.tree_node_id] ||
    resolveNodeNameWithFallback(
      getNodeId(finalSectionForName, ['tree_node_id', 'section_id_ref']),
      originalIndexMap,
      finalSectionForName,
      'Section'
    );

  let targetSectionFinalName = '';
  if (position !== 'into' && movePosition?.targetId) {
    // For copy operations, use targetAllNodes (original structure) to find the target section
    // This ensures we get the correct section name before the copy operation changed the structure
    const targetNodesToSearch = isCopyOperation ? targetAllNodes : newContent;
    const targetSection = findSectionById(targetNodesToSearch, movePosition.targetId);
    if (targetSection) {
      // For copy operations, use pastePosition.targetIndex if available (the index selected by user)
      // Otherwise use targetIndexMap to get the section name from the original structure
      if (isCopyOperation && movePosition?.targetIndex !== null && movePosition?.targetIndex !== undefined) {
        // Use the index that was selected by the user (0-based, so add 1)
        targetSectionFinalName = `Section ${movePosition.targetIndex + 1}`;
      } else {
        // Use the appropriate index map based on operation type
        const indexMapToUse = isCopyOperation ? targetIndexMap : finalIndexMap;
        targetSectionFinalName = indexMapToUse[targetSection.tree_node_id] ||
          resolveNodeNameWithFallback(
            getNodeId(targetSection, ['tree_node_id', 'section_id_ref']),
            isCopyOperation ? targetIndexMap : originalIndexMap,
            targetSection,
            'Section'
          );
      }
    }
  }

  const destinationBase = `${targetInfo.partNumber}, Rev ${targetInfo.revision}`;
  const actionPrefix = buildActionPrefix(originalSectionName, position, targetSectionFinalName, destinationBase, sourceInfo, targetInfo);

  // For copy operations, find the copied section (not the original)
  const finalSectionForIndexing = actionType === 'Copy' 
    ? findFinalSection(newContent, sourceSection, position, movePosition, true)
    : finalSection;
  
  if (!finalSectionForIndexing) return null;

  const hierarchyNodes = collectSectionHierarchyNodes(finalSectionForIndexing);
  if (hierarchyNodes.length === 0) return null;

  const { stepLookup, substepLookup } = buildLookupMaps(finalSectionForIndexing);
  const childTypeCounters = { attachment: 0, canvas: 0, 'rich-text': 0, video: 0, form: 0 };
  // Filter out nodes without IDs and nodes that can't be found in finalSection
  // This ensures totalOperations only counts nodes that will actually be processed
  const validNodes = hierarchyNodes.filter(item => {
    if (!item.id) return false;
    const nodeData = findNodeInSectionForCopy(finalSectionForIndexing, item.id, item.type);
    return !!nodeData;
  });
  const totalOperations = validNodes.length;
  const nodeIndexing = {};
  const isMoveOperation = actionType === 'Move';

  const baseStructure = cloneStructure(Array.isArray(targetAllNodes) ? targetAllNodes : []);
  // For move operations, blank the source section's steps array
  // For copy operations, keep the original section and add a new copy incrementally
  const initialStructure = isMoveOperation
    ? setBlankSection(baseStructure, sourceSection)
    : baseStructure;
  let currentStructure = isMoveOperation ? initialStructure.filter(item => item.version_id_ref !== sourceSection.version_id_ref) : initialStructure;
  
  // Track the new copied section's tree_node_id for same-document copies
  // This helps us identify which section is the new copy vs the original
  let newCopiedSectionTreeNodeId = null;
  
  // Track the previous newContent state for copy operations
  // originalContent for each operation should be the newContent from the previous operation
  let previousNewContent = null;

  // Use a separate counter for operation numbers that only increments when we create an entry
  let operationCounter = 0;
  
  const elementTitles = generateTitlesFromStructure(sourceAllNodes);

  hierarchyNodes.forEach((item, index) => {
    const { id, node, type, parentStepId, parentSubstepId } = item;
    if (!id) return;

    // Check if we can find the node data before incrementing counter
    // This ensures we only count operations that will actually be processed
    const nodeData = findNodeInSectionForCopy(finalSectionForIndexing, id, type);
    if (!nodeData) return; // Skip if node can't be found

    operationCounter++; // Increment only when we process a valid node that can be found

    const originalName = elementTitles[node.tree_node_id];
    const finalStepName = parentStepId && finalIndexMap[parentStepId]
      ? finalIndexMap[parentStepId]
      : parentStepId && stepLookup.has(parentStepId)
      ? resolveNodeNameWithFallback(parentStepId, originalIndexMap, stepLookup.get(parentStepId), 'Step')
      : 'the target step';

    const finalSubstepName = parentSubstepId && finalIndexMap[parentSubstepId]
      ? finalIndexMap[parentSubstepId]
      : parentSubstepId && substepLookup.has(parentSubstepId)
      ? resolveNodeNameWithFallback(parentSubstepId, originalIndexMap, substepLookup.get(parentSubstepId), 'Substep')
      : toLowerCaseFirst(finalStepName);

    const movementPhrase = buildMovementPhrase(
      type, position, destinationBase, finalSectionName, finalStepName, finalSubstepName, targetSectionFinalName
    );

    const title = `${actionPrefix} System Performs Operation ${operationCounter} of ${totalOperations}, ${actionType} ${originalName} ${movementPhrase}.`;

    // For copy operations, originalContent should be the previous newContent (state after previous operation)
    // For the first operation, use currentStructure (state before adding the section)
    let originalContent = null;
    if (isCopyOperation) {
      if(previousNewContent){
        originalContent = JSON.parse(JSON.stringify(previousNewContent));
      }
      else {
        originalContent = structuredClone(currentStructure || []);
      }
    } 
    
    // Add the node to structure
    currentStructure = addNodeToSectionStructure({
      currentStructure,
      hierarchyNode: item,
      finalSection: finalSectionForIndexing,
      position,
      movePosition,
      isCopyOperation,
    });
    
    // Track the new copied section's tree_node_id when we add it in the first operation
    // Do this BEFORE filtering newContent so we can use it in the filter
    if (isCopyOperation && type === 'section') {
      // Find the newly added section in currentStructure by looking for the dummy tree_node_id
      // The new section will have a tree_node_id starting with "copy-"
      const sourceSectionId = getNodeId(sourceSection, ['tree_node_id', 'section_id_ref']);
      const addedSection = currentStructure.find(sec => {
        const secId = getNodeId(sec, ['tree_node_id', 'section_id_ref']);
        // Check if this section matches the source ID and has a dummy tree_node_id (starts with "copy-")
        return secId === sourceSectionId && sec.tree_node_id && sec.tree_node_id.startsWith('copy-');
      });
      if (addedSection && addedSection.tree_node_id) {
        newCopiedSectionTreeNodeId = addedSection.tree_node_id;
      }
    }
    
    // Capture newContent after adding the node (full document structure)
    // For copy operations, keep all sections (including the original section)
    // The new copied section will be added incrementally
    const newContentState = structuredClone(currentStructure || []);
    if(!isCopyOperation){
      if(index === 0) {
        originalContent = structuredClone(initialStructure);
      }else{
        originalContent = JSON.parse(JSON.stringify(previousNewContent)).map(item => {
          if(item.version_id_ref === sourceSection.version_id_ref) {
            item = newContentState.find(item => item.version_id_ref === sourceSection.version_id_ref);
          }
          return item;
        });
      }
    }

    nodeIndexing[id] = {
      title,
      originalContent: originalContent || [],
      newContent: newContentState || [],
    };
    
    // Update previousNewContent for the next operation
    // Store originalContent (which contains the updated sourceSection state) so it can be used as the base for the next operation's originalContent
    if(isCopyOperation) {
      previousNewContent = JSON.parse(JSON.stringify(newContentState || []));
    }else{
      previousNewContent = JSON.parse(JSON.stringify(originalContent || []));
    }
  });

  return Object.keys(nodeIndexing).length ? nodeIndexing : null;
};

export const buildNodeIndexingForSectionMove = ({
  sourceSection,
  sourceAllNodes,
  newContent,
  targetAllNodes = [],
  targetDoc,
  position,
  movePosition,
}) => {
  return buildNodeIndexingCore({
    sourceSection,
    sourceAllNodes,
    newContent,
    targetAllNodes,
    targetDoc,
    sourceDoc: null,
    position,
    movePosition,
    actionType: 'Move',
    buildActionPrefix: (originalSectionName, position, targetSectionFinalName, destinationBase) => {
      const positionDescriptor = position === 'into'
        ? `into ${destinationBase}`
        : position === 'above'
        ? `above ${targetSectionFinalName || 'the target section'} in ${destinationBase}`
        : `below ${targetSectionFinalName || 'the target section'} in ${destinationBase}`;
      return `User Moved ${originalSectionName} ${positionDescriptor}.`;
    },
  });
};

export const buildNodeIndexingForSectionCopy = ({
  sourceSection,
  sourceAllNodes,
  newContent,
  targetAllNodes = [],
  targetDoc,
  sourceDoc,
  position,
  movePosition,
}) => {
  return buildNodeIndexingCore({
    sourceSection,
    sourceAllNodes,
    newContent,
    targetAllNodes,
    targetDoc,
    sourceDoc,
    position,
    movePosition,
    actionType: 'Copy',
    buildActionPrefix: (originalSectionName, position, targetSectionFinalName, destinationBase, sourceInfo, targetInfo) => {
      const sourceDocInfo = sourceInfo ? `from ${sourceInfo.partNumber}, Rev ${sourceInfo.revision}` : '';
      const targetDocInfo = `into ${targetInfo.partNumber}, Rev ${targetInfo.revision}`;
      
      const positionDescriptor = position === 'into'
        ? targetDocInfo
        : position === 'above'
        ? `above ${targetSectionFinalName || 'the target section'} ${targetDocInfo}`
        : `below ${targetSectionFinalName || 'the target section'} ${targetDocInfo}`;
      
      return `User Copied ${originalSectionName} ${sourceDocInfo} ${positionDescriptor}.`;
    },
  });
};

export const prepareCopyNewContent = ({
  targetAllNodes,
  sourceSection,
  targetDoc,
  position,
  movePosition,
  toSectionIdRef,
}) => {
  let newContent = structuredClone(targetAllNodes);
  const sourceSectionClone = structuredClone(sourceSection);
  
  if (position === 'into') {
    newContent.push(sourceSectionClone);
  } else {
    const targetIndex = newContent.findIndex(
      section => section.section_id_ref === toSectionIdRef || section.tree_node_id === movePosition.targetId
    );
    
    if (targetIndex !== -1) {
      newContent.splice(position === 'above' ? targetIndex : targetIndex + 1, 0, sourceSectionClone);
    }
  }

  ensureSequentialSectionOrder(newContent);
  return newContent;
};

export const calculateMoveParameters = ({
  position,
  targetAllNodes,
  targetDoc,
  movePosition,
  moveCreateCopy,
  sourceSection,
  moveDocument,
}) => {
  if (position === 'into') {
    const sortedSections = [...targetAllNodes].sort((a, b) => {
      const orderA = getSiblingOrderNumber(a);
      const orderB = getSiblingOrderNumber(b);
      return orderA - orderB;
    });
    const highestOrder = sortedSections.length > 0 ? getSiblingOrderNumber(sortedSections[sortedSections.length - 1]) : 0;
    
    return {
      toOrder: highestOrder + 1,
      toProcedureTreeNodeRef: sortedSections.length > 0 ? sortedSections[sortedSections.length - 1].tree_node_id : null,
      toSectionIdRef: null,
      direction: moveCreateCopy ? 'paste_down' : 'move_down',
      direction_move_into: moveCreateCopy ? undefined : true,
    };
  }

  const targetSection = targetAllNodes.find(section => section.tree_node_id === movePosition.targetId);
  if (!targetSection) {
    throw new Error('Target section not found.');
  }

  const targetOrder = getSiblingOrderNumber(targetSection);
  const sourceOrder = sourceSection ? getSiblingOrderNumber(sourceSection) : null;
  
  const isSameDocumentMove = !moveCreateCopy && sourceSection && moveDocument &&
    moveDocument.partId === targetDoc.partId && moveDocument.revisionId === targetDoc.revisionId;
  
  let toOrder;
  if (isSameDocumentMove && sourceOrder !== null) {
    if (sourceOrder < targetOrder) {
      toOrder = position === 'above' ? targetOrder - 1 : targetOrder;
    } else {
      toOrder = position === 'above' ? targetOrder : targetOrder + 1;
    }
  } else {
    toOrder = position === 'above' ? targetOrder : targetOrder + 1;
  }
  
  return {
    toOrder,
    toProcedureTreeNodeRef: targetSection.tree_node_id,
    toSectionIdRef: targetSection.section_id_ref,
    direction: moveCreateCopy
      ? (position === 'above' ? 'paste_up' : 'paste_down')
      : (position === 'above' ? 'move_up' : 'move_down'),
    direction_move_into: moveCreateCopy ? undefined : false,
  };
};

export const prepareMoveNewContent = ({
  targetAllNodes,
  sourceSection,
  moveDocument,
  targetDoc,
  position,
  movePosition,
  toSectionIdRef,
}) => {
  let newContent = structuredClone(targetAllNodes);
  const sourceSectionClone = structuredClone(sourceSection);
  
  const isSameDocument = moveDocument.partId === targetDoc.partId && moveDocument.revisionId === targetDoc.revisionId;
  
  if (isSameDocument) {
    newContent = newContent.filter(section => section.tree_node_id !== sourceSection.tree_node_id);
  }
  
  if (position === 'into') {
    newContent.push(sourceSectionClone);
  } else {
    const targetIndex = newContent.findIndex(
      section => section.section_id_ref === toSectionIdRef || section.tree_node_id === movePosition.targetId
    );
    
    if (targetIndex !== -1) {
      newContent.splice(position === 'above' ? targetIndex : targetIndex + 1, 0, sourceSectionClone);
    }
  }

  ensureSequentialSectionOrder(newContent);
  return newContent;
};

export const buildCopyVersionContent = ({
  targetAllNodesOriginal,
  newContent,
  targetDoc,
  nodeIndexing, 
}) => {
  // Extract actionToShow from the first node_indexing entry
  // Get the text before "System Performs Operation"
  let actionToShow = "";
  if (nodeIndexing && typeof nodeIndexing === 'object' && Object.keys(nodeIndexing).length > 0) {
    // Get the first entry from nodeIndexing
    const firstEntryKey = Object.keys(nodeIndexing)[0];
    const firstEntry = nodeIndexing[firstEntryKey];
    if (firstEntry && firstEntry.title) {
      // Extract the part before "System Performs Operation"
      const systemPerformsIndex = firstEntry.title.indexOf("System Performs Operation");
      if (systemPerformsIndex !== -1) {
        actionToShow = firstEntry.title.substring(0, systemPerformsIndex).trim();
      } else {
        // If "System Performs Operation" not found, use the entire title
        actionToShow = firstEntry.title.trim();
      }
    }
  }

  return {
    action_to_show: actionToShow,
    rev: targetDoc.revision || '',
    status: targetDoc.status || 'Not Submitted',
    item_code_type: targetDoc.type || '',
    partNumber: targetDoc.partNumber || '',
    partDescription: targetDoc.description || '',
    main_entry: true,
  }
};

export const buildMoveVersionContent = async ({
  moveNode,
  position,
  targetDoc,
  targetAllNodes,
  movePosition,
  targetAllNodesOriginal,
  newContent,
}) => {
  const moveTypeText = position === 'into' ? 'into' : position === 'above' ? 'up' : 'below';
  
  const targetSectionIndex = position !== 'into' 
    ? targetAllNodes.findIndex(s => s.tree_node_id === movePosition.targetId) + 1 
    : '';
  
  const actionToShow = position === 'into'
    ? `User Moved ${moveNode.nodeTitle || 'Section'} into ${targetDoc.partNumber || ''}, Rev ${targetDoc.revision || ''}`
    : `User Moved ${moveNode.nodeTitle || 'Section'} ${position === 'above' ? 'above' : 'below'} Section ${targetSectionIndex} in ${targetDoc.partNumber || ''}, Rev ${targetDoc.revision || ''}`;

  return {
    action_to_show: actionToShow,
    moveType: moveTypeText,
    rev: targetDoc.revision || '',
    status: targetDoc.status || 'Not Submitted',
    item_code_type: targetDoc.type,
    partNumber: targetDoc.partNumber || '',
    partDescription: targetDoc.description || '',
    move_copy_action: position === 'into'
      ? `${targetDoc.type || ''} ${targetDoc.partNumber || ''} in ${targetDoc.partNumber || ''}, Rev ${targetDoc.revision || ''}`
      : `Section ${targetSectionIndex} in ${targetDoc.partNumber || ''}, Rev ${targetDoc.revision || ''}`,
    main_entry: true,
  };
};

const cleanupPayload = (payload, position, isMove = false) => {
  Object.keys(payload).forEach((key) => {
    const value = payload[key];
    
    if (value === "null" || value === null) {
      if (key === 'to_section_id_ref' && position === 'into') {
        // Keep null for INTO
      } else if (isMove && ['from_step_id_ref', 'from_substep_id_ref', 'to_step_id_ref', 'to_substep_id_ref'].includes(key)) {
        // Keep null for these fields in move operations
      } else {
        delete payload[key];
      }
    } else if (value === "" && key === 'to_section_id_ref' && position === 'into') {
      delete payload[key];
    }
  });
};

export const cleanupCopyPayload = (copyPayload, position) => {
  cleanupPayload(copyPayload, position, false);
};

export const cleanupMovePayload = (movePayload, position) => {
  cleanupPayload(movePayload, position, true);
};

export const buildCopyPayload = async ({
  sourceSection,
  moveDocument,
  targetDoc,
  position,
  movePosition,
  toOrder,
  toProcedureTreeNodeRef,
  toSectionIdRef,
  direction,
  moveComment,
  targetAllNodesOriginal,
  newContent,
  sourceAllNodes,
}) => {
  
  const audit_trail_data = await getAuditTrailData({
    action: "SECTION_COPIED",
    comment: moveComment,
    parent_version: null,
    version_content: {},
  }, true);

  const copyPayload = {
    from_procedure_tree_node_ref: sourceSection.tree_node_id,
    from_order: getSiblingOrderNumber(sourceSection),
    from_type: 'section',
    from_part_id_ref: moveDocument.partId,
    from_procedure_rev_ref: moveDocument.revisionId,
    from_section_id_ref: sourceSection.section_id_ref,
    from_step_id_ref: "",
    from_substep_id_ref: "",
    to_procedure_tree_node_ref: toProcedureTreeNodeRef,
    to_order: parseInt(toOrder || 0, 10),
    to_type: 'section',
    to_part_id_ref: targetDoc.partId,
    to_procedure_rev_ref: targetDoc.revisionId,
    to_section_id_ref: position === 'into' ? null : (toSectionIdRef || ''),
    to_step_id_ref: "",
    to_substep_id_ref: "",
    direction: direction,
    audit_trail_data: audit_trail_data,
  };

  // Generate node_indexing for copy operation
  const nodeIndexing = buildNodeIndexingForSectionCopy({
    sourceSection,
    sourceAllNodes: sourceAllNodes || [],
    newContent: newContent || [],
    targetAllNodes: targetAllNodesOriginal || [],
    targetDoc,
    sourceDoc: moveDocument,
    position,
    movePosition,
  });

  if (nodeIndexing && Object.keys(nodeIndexing).length > 0) {
    copyPayload.node_indexing = nodeIndexing;
  }

  const version_content = buildCopyVersionContent({
    targetAllNodesOriginal,
    newContent,
    targetDoc,
    nodeIndexing
  });

  copyPayload.audit_trail_data.version_content = version_content;

  cleanupCopyPayload(copyPayload, position);
  return copyPayload;
};

export const buildMovePayload = async ({
  sourceSection,
  moveDocument,
  targetDoc,
  position,
  movePosition,
  moveNode,
  toOrder,
  toProcedureTreeNodeRef,
  toSectionIdRef,
  direction,
  direction_move_into,
  moveComment,
  targetAllNodesOriginal,
  sourceAllNodes,
  newContent,
}) => {
  const parent_version = sourceSection.version_id_ref || null;
  const version_content = await buildMoveVersionContent({
    moveNode,
    position,
    targetDoc,
    targetAllNodes: targetAllNodesOriginal,
    movePosition,
    targetAllNodesOriginal,
    newContent,
  });

  const audit_trail_data = await getAuditTrailData({
    action: "SECTION_MOVED",
    comment: moveComment,
    parent_version: parent_version,
    version_content: version_content,
  }, false);

  const movePayload = {
    from_procedure_tree_node_ref: sourceSection.tree_node_id,
    from_order: getSiblingOrderNumber(sourceSection),
    from_type: 'section',
    from_part_id_ref: moveDocument.partId,
    from_procedure_rev_ref: moveDocument.revisionId,
    from_section_id_ref: sourceSection.section_id_ref,
    from_step_id_ref: null,
    from_substep_id_ref: null,
    to_procedure_tree_node_ref: toProcedureTreeNodeRef,
    to_order: parseInt(toOrder || 0, 10),
    to_type: 'section',
    to_part_id_ref: targetDoc.partId,
    to_procedure_rev_ref: targetDoc.revisionId,
    to_section_id_ref: position === 'into' ? null : (toSectionIdRef || null),
    to_step_id_ref: null,
    to_substep_id_ref: null,
    direction_move_into: direction_move_into,
    direction: direction,
    create_copy: false,
    audit_trail_data: audit_trail_data,
  };

  cleanupMovePayload(movePayload, position);

  const nodeIndexing = buildNodeIndexingForSectionMove({
    sourceSection,
    sourceAllNodes,
    newContent,
    targetAllNodes: targetAllNodesOriginal,
    targetDoc,
    position,
    movePosition,
  });

  if (nodeIndexing && Object.keys(nodeIndexing).length > 0) {
    movePayload.node_indexing = nodeIndexing;
  }

  return movePayload;
};
